import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import { DocumentRevisionStatus, useListUrlSync } from '@/shared/lib';
import type { SortOrder } from '@/shared/lib';
import { useTableFilterModal } from '@/shared/lib/use';
import { listRecords } from '../api/records.api';
import RecordListFilterModal from '../ui/RecordListFilterModal.vue';
import {
  DEFAULT_RECORD_QUANTITY,
  DEFAULT_RECORD_SEARCH_TYPE,
  RECORD_SEARCH_FIELDS,
  RECORD_SORT_FIELDS,
  isRecordSortField,
  parseRecordListFilters,
  parseRecordQuantity,
  parseRecordTerm,
} from '../lib/record-list-query';
import type {
  RecordListFilters,
  RecordSearchType,
  RecordStatusFilter,
} from '../lib/record-list-query';
import { toRecordListRow } from './records.types';
import type { RecordListRow } from './records.types';
import { useRecordSearch } from './useRecordSearch';
import type { RecordFetcher } from './useRecordSearch';
import { useRecordSelection } from './useRecordSelection';
import { useRecordExpansion } from './useRecordExpansion';

const SEARCH_FIELD_IDS = RECORD_SEARCH_FIELDS.map((field) => field.id);

/**
 * The status filter of the screen against the status a row carries. It mirrors the mapping the
 * endpoint applies, so the listing can tell on its own whether a row it has just changed still
 * answers the filter in force.
 */
const STATUS_FILTER_TO_REVISION_STATUS: Record<RecordStatusFilter, DocumentRevisionStatus> = {
  vigente: DocumentRevisionStatus.CURRENT,
  obsoleto: DocumentRevisionStatus.OBSOLETE,
  inativo: DocumentRevisionStatus.INACTIVE,
};

/**
 * Single owner of the record listing state.
 *
 * `useListUrlSync` answers for term, search field, ordering and page, both when
 * hydrating and when writing the URL. Quantity per page and filters are not
 * covered by it, so they are born here, hydrated from the query itself.
 * `useRecordSearch` is subordinate: it handles the request, the continuous
 * scroll and the race guard, and the page it keeps internally never reaches the
 * URL, because the continuous scroll increments it on every batch.
 *
 * Every mutation goes through `commit`, which fetches and only then writes the
 * URL, so the controls and the URL never diverge.
 */
export function useRecordList() {
  const route = useRoute();

  const { searchTerm, searchFieldId, sort, order, currentPage, syncUrl } = useListUrlSync({
    fields: SEARCH_FIELD_IDS,
    defaultField: DEFAULT_RECORD_SEARCH_TYPE,
    fieldQueryKey: 'searchType',
    validSorts: RECORD_SORT_FIELDS,
    defaultOrder: 'asc',
  });

  searchTerm.value = parseRecordTerm(route.query.term);

  const filters = ref<RecordListFilters>(parseRecordListFilters(route.query));

  const activeSort = computed(() => (isRecordSortField(sort.value) ? sort.value : undefined));

  const activeOrder = computed<SortOrder>(() => (order.value === 'desc' ? 'desc' : 'asc'));

  const fetchRecords: RecordFetcher<RecordListRow> = async ({ page, itemsPerPage }) => {
    const term = searchTerm.value.trim();

    const { data, metadata } = await listRecords({
      page,
      itemsPerPage,
      term: term || undefined,
      searchType: term ? (searchFieldId.value as RecordSearchType) : undefined,
      sort: activeSort.value,
      order: activeSort.value ? activeOrder.value : undefined,
      status: filters.value.status,
      generatedRecords: filters.value.generatedRecords,
    });

    return { data: data.map(toRecordListRow), metadata };
  };

  const search = useRecordSearch<RecordListRow>(computed(() => fetchRecords));

  search.selectedQuantity.value = parseRecordQuantity(route.query.itemsPerPage);

  /**
   * `initializing` covers the whole hydration, including the clamp of a page past
   * the total: only in that window the screen has no card to show yet. Without it
   * the empty state flashes before the first response. After it, `loading` is
   * what signals a fetch, and the card stays mounted.
   */
  const initializing = ref(true);

  const loading = computed(() => initializing.value || search.loading.value);

  const visibleRecords = computed(() =>
    search.isShowAllMode.value ? search.allItems.value : search.items.value
  );

  const visibleIds = computed(() => visibleRecords.value.map((record) => record.id));

  /**
   * Any change to what the listing shows invalidates a selection of ids, so the
   * reset key covers the whole query, not only the three minimum triggers.
   */
  const listingKey = computed(() =>
    [
      currentPage.value,
      search.selectedQuantity.value,
      searchTerm.value.trim(),
      searchFieldId.value,
      sort.value,
      order.value,
      filters.value.status ?? '',
      filters.value.generatedRecords ?? '',
    ].join('|')
  );

  const selection = useRecordSelection(
    () => visibleIds.value.length,
    () => listingKey.value
  );

  // The expansion follows the same reset key as the selection: both hold ids that
  // stop meaning anything once the visible set changes.
  const expansion = useRecordExpansion(() => listingKey.value);

  /**
   * Previous revisions are selectable, and their ids live in their own set. Record
   * ids and revision ids come from different tables, so a single set of numbers
   * could not tell one from the other and the deletion would not know what to act
   * on. The header checkbox stays about records, which is what it counts.
   *
   * Collapsing a row keeps its revisions selected: the user reopens the row and
   * finds the marks where they left them. What clears the selection is a change to
   * the query, the same reset the records follow.
   */
  // The total is zero because there is no notion of "every revision selected": the
  // header checkbox never covers the revisions, only the records of the page.
  const revisionSelection = useRecordSelection(
    () => 0,
    () => listingKey.value
  );

  const hasSelection = computed(
    () => selection.hasSelection.value || revisionSelection.hasSelection.value
  );

  const selectedCount = computed(
    () => selection.selectedCount.value + revisionSelection.selectedCount.value
  );

  const isIndeterminate = computed(() => hasSelection.value && !selection.allSelected.value);

  const hasActiveSearch = computed(() =>
    Boolean(searchTerm.value.trim() || filters.value.status || filters.value.generatedRecords)
  );

  const syncListUrl = () =>
    syncUrl({
      ...(search.selectedQuantity.value !== DEFAULT_RECORD_QUANTITY
        ? { itemsPerPage: search.selectedQuantity.value }
        : {}),
      ...(filters.value.status ? { status: filters.value.status } : {}),
      ...(filters.value.generatedRecords
        ? { generatedRecords: filters.value.generatedRecords }
        : {}),
    });

  const commit = async (fetchStep: () => Promise<unknown>) => {
    await fetchStep();
    syncListUrl();
  };

  const handleSearch = (term: string) =>
    commit(() => {
      searchTerm.value = term;
      currentPage.value = 1;
      return search.refetch();
    });

  const handleQuantityChange = (value: string | number) =>
    commit(() => {
      currentPage.value = 1;
      return search.handleQuantityChange(value);
    });

  const handlePageChanged = (page: number) =>
    commit(() => {
      currentPage.value = page;
      return search.onPageChanged(page);
    });

  const handleSortUpdate = (sortField: string, sortOrder: string) =>
    commit(() => {
      const nextSort = isRecordSortField(sortField) ? sortField : '';
      const nextOrder: SortOrder = sortOrder === 'desc' ? 'desc' : 'asc';

      sort.value = nextSort;
      order.value = nextSort ? nextOrder : '';
      currentPage.value = 1;
      return search.refetch();
    });

  const handleFilterUpdate = (next: RecordListFilters) =>
    commit(() => {
      filters.value = { status: next.status, generatedRecords: next.generatedRecords };
      currentPage.value = 1;
      return search.refetch();
    });

  const { isFilterActive, openFilterModal } = useTableFilterModal<RecordListFilters>(
    RecordListFilterModal,
    { clearFilters: () => undefined, filters: filters.value },
    handleFilterUpdate,
    () => filters.value,
    () => ({
      onClearFilters: () => void handleFilterUpdate({}),
    })
  );

  /**
   * The header checkbox is a "has selection" switch: with anything checked it
   * clears, which matches the dash of the indeterminate state, and it only
   * selects the whole page when nothing is checked. It clears the revisions too,
   * otherwise a selection the header says it wiped would survive out of sight.
   */
  const toggleAll = () => {
    if (hasSelection.value) {
      selection.clear();
      revisionSelection.clear();
      return;
    }

    selection.selectAll(visibleIds.value);
  };

  /**
   * Optimistic status change of one row, returning the rollback the caller runs when the write
   * fails, so the snapshot never leaves this composable.
   *
   * Both fields are written because neither alone describes the row: the switch reads `isActive`
   * and the badge reads `revisionStatus`, and a row activated from `INACTIVE` would otherwise
   * flip the switch and keep the badge on "Inativo". The statuses are the ones the endpoint
   * writes: inactivating stores INACTIVE, activating promotes the revision to CURRENT.
   */
  const applyRecordStatus = (id: number, isActive: boolean) => {
    const previous = search.findItem(id);

    search.patchItem(id, {
      isActive,
      revisionStatus: isActive ? DocumentRevisionStatus.CURRENT : DocumentRevisionStatus.INACTIVE,
    });

    return () => {
      if (!previous) return;

      search.patchItem(id, {
        isActive: previous.isActive,
        revisionStatus: previous.revisionStatus,
      });
    };
  };

  /**
   * Runs once the write landed, for the two things the row alone cannot answer for.
   *
   * Activating makes the revision the current one of the code and obsoletes the one that was
   * current, which may be a previous revision of this same row: that one cell is rewritten in the
   * expansion cache. The cache is NOT dropped, because the set of previous revisions is the
   * revisions with a lower ordinal and no status write moves an ordinal, so refetching would
   * reload the same list the open row already shows. Inactivating touches no sibling at all.
   *
   * And a row that no longer answers the status filter leaves the listing instead of standing
   * there contradicting the filter the user set.
   */
  const settleRecordStatus = (id: number) => {
    const row = search.findItem(id);
    const status = filters.value.status;
    const staysInTheListing =
      !row || !status || row.revisionStatus === STATUS_FILTER_TO_REVISION_STATUS[status];

    if (staysInTheListing) {
      if (row?.isActive) {
        expansion.patchRevisions(id, (revision) =>
          revision.revisionStatus === DocumentRevisionStatus.CURRENT
            ? { ...revision, revisionStatus: DocumentRevisionStatus.OBSOLETE }
            : revision
        );
      }

      return;
    }

    // The revisions of a row that leaves go with it. They are read before the cache is dropped,
    // because afterwards there is no way left to tell which ids were its own.
    for (const revision of expansion.getRevisionsState(id).revisions) {
      revisionSelection.remove(revision.id);
    }

    search.removeItem(id);
    expansion.remove(id);
    selection.remove(id);
  };

  /**
   * A write can change which revision heads a code, so the expansion cache is dropped whole before
   * refetching: a cached list built for the previous head is what makes a revision show up twice.
   */
  const refresh = () =>
    commit(async () => {
      expansion.collapseAll();
      selection.clear();
      revisionSelection.clear();

      // `refetch` always goes back to page 1, which is right for the continuous scroll and wrong
      // for a paged listing, where the user stays where they were.
      if (search.isShowAllMode.value) {
        await search.refetch();
        return;
      }

      await search.onPageChanged(currentPage.value);

      const totalPages = Math.max(search.metadata.value?.totalPages ?? 0, 1);
      if (currentPage.value <= totalPages) return;

      currentPage.value = totalPages;
      await search.onPageChanged(totalPages);
    });

  const initialize = async () => {
    try {
      // In "show all" mode the listing loads in batches and the page is not
      // navigable: hydrating a page greater than 1 would leave the first batch
      // unreachable, because the continuous scroll only moves forward.
      if (search.isShowAllMode.value) {
        currentPage.value = 1;
        await search.refetch();
      } else if (currentPage.value > 1) {
        await search.onPageChanged(currentPage.value);

        const totalPages = search.metadata.value?.totalPages ?? 0;
        if (currentPage.value > Math.max(totalPages, 1)) {
          currentPage.value = 1;
          await search.refetch();
        }
      } else {
        await search.refetch();
      }
    } finally {
      initializing.value = false;
    }

    syncListUrl();
  };

  return {
    searchTerm,
    searchFieldId,
    sort,
    order,
    filters,
    records: visibleRecords,
    items: search.items,
    allItems: search.allItems,
    metadata: search.metadata,
    loading,
    initializing,
    loadingMore: search.loadingMore,
    hasMoreItems: search.hasMoreItems,
    selectedQuantity: search.selectedQuantity,
    isShowAllMode: search.isShowAllMode,
    selectedIds: selection.selectedIds,
    selectedRevisionIds: revisionSelection.selectedIds,
    selectedCount,
    hasSelection,
    allSelected: selection.allSelected,
    isIndeterminate,
    isSelected: selection.isSelected,
    isRevisionSelected: revisionSelection.isSelected,
    toggleSelection: selection.toggle,
    toggleRevisionSelection: revisionSelection.toggle,
    toggleAll,
    expandedIds: expansion.expandedIds,
    isExpanded: expansion.isExpanded,
    getRevisionsState: expansion.getRevisionsState,
    toggleExpansion: expansion.toggle,
    retryRevisions: expansion.retry,
    hasActiveSearch,
    isFilterActive,
    openFilterModal,
    handleSearch,
    handleQuantityChange,
    handlePageChanged,
    handleSortUpdate,
    handleFilterUpdate,
    applyRecordStatus,
    settleRecordStatus,
    initialize,
    refresh,
  };
}
